/**
 * @fileoverview Success page after completing project application submission
 * @module routes/onboarding/success
 */

import type { ReactElement } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { PrimaryActionButton } from '@/components/ui/PrimaryActionButton';
import { useUserProjectApplication } from '@/features/onboarding/hooks/useProjectApplication';

/**
 * Success page component for completed project applications.
 *
 * Displays confirmation message and application status after successful submission.
 * Provides navigation options for next steps in the user journey.
 *
 * Features:
 * - Success confirmation with visual feedback
 * - Application status display
 * - Navigation to dashboard or home
 * - Loading state while fetching application data
 */
function OnboardingSuccess(): ReactElement {
  const router = useRouter();
  const { data: application, isLoading } = useUserProjectApplication();

  /**
   * Handle navigation to the main dashboard.
   */
  const handleGoToDashboard = (): void => {
    router.navigate({ to: '/' });
  };

  /**
   * Handle navigation to deals page.
   */
  const handleExploreBrowse = (): void => {
    router.navigate({ to: '/deals' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#44FF02] mx-auto mb-4"></div>
          <p className="text-white">Loading your application...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center px-4">
      <div className="w-full max-w-[600px] text-center">
        {/* Success Icon */}
        <div className="mb-8">
          <div className="w-20 h-20 bg-[#44FF02] rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Success Message */}
        <h1
          className="text-[48px] font-bold leading-[56px] mb-6"
          style={{ color: '#44FF02' }}
        >
          Application Submitted!
        </h1>

        <p
          className="text-[18px] leading-[28px] mb-8 max-w-[480px] mx-auto"
          style={{
            color: '#B3B3B3',
            fontFamily: '"Nunito Sans"'
          }}
        >
          Thank you for submitting your project application to Onicorn. Our team will review your submission and get back to you within 3-5 business days.
        </p>

        {/* Application Details */}
        {application && (
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-lg p-6 mb-8 text-left">
            <h3 className="text-white font-semibold mb-4">Application Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#B3B3B3]">Project Name:</span>
                <span className="text-white">{application.project_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#B3B3B3]">Token Symbol:</span>
                <span className="text-white">{application.token_symbol}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#B3B3B3]">Status:</span>
                <span className="text-[#44FF02] capitalize">{application.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#B3B3B3]">Submitted:</span>
                <span className="text-white">
                  {application.submitted_at ?
                    new Date(application.submitted_at).toLocaleDateString() :
                    'Just now'
                  }
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="bg-[#1A1A1A] border border-[#282828] rounded-lg p-6 mb-8 text-left">
          <h3 className="text-white font-semibold mb-4">What's Next?</h3>
          <ul className="space-y-3 text-sm text-[#B3B3B3]">
            <li className="flex items-start">
              <span className="text-[#44FF02] mr-2">1.</span>
              Our team will review your application and verify the provided information
            </li>
            <li className="flex items-start">
              <span className="text-[#44FF02] mr-2">2.</span>
              We may reach out for additional documentation or clarification
            </li>
            <li className="flex items-start">
              <span className="text-[#44FF02] mr-2">3.</span>
              You'll receive an email notification with our decision
            </li>
            <li className="flex items-start">
              <span className="text-[#44FF02] mr-2">4.</span>
              If approved, your project will be listed on our platform
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <PrimaryActionButton onClick={handleGoToDashboard}>
            Go to Dashboard
          </PrimaryActionButton>

          <button
            onClick={handleExploreBrowse}
            className="w-full h-[40px] bg-transparent border border-[#44FF02] text-[#44FF02] rounded-[15px] font-semibold transition-all duration-300 hover:bg-[#44FF02] hover:text-black"
            style={{
              fontFamily: '"Nunito Sans"',
              fontSize: '18px',
            }}
          >
            Browse Other Projects
          </button>
        </div>

        {/* Support Contact */}
        <p
          className="text-[14px] leading-[20px] mt-8"
          style={{
            color: '#777777',
            fontFamily: '"Nunito Sans"'
          }}
        >
          Have questions about your application?{' '}
          <a
            href="mailto:support@onicorn.com"
            className="text-[#44FF02] hover:underline"
          >
            Contact our support team
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * File route configuration for the onboarding success page.
 *
 * Requires authentication to access the page.
 * Redirects unauthenticated users to login.
 */
export const Route = createFileRoute('/onboarding/success')({
  component: OnboardingSuccess,
  beforeLoad: () => {
    // Authentication check can be handled at the router level or component level
    // For now, allowing access without beforeLoad auth check
  },
});